import { ConfigCatProvider, LogLevel } from 'configcat-react';
import { cloneElement, lazy, Suspense } from 'react';
import { Route, Routes, useLocation } from 'react-router-dom';
import TrackingContextProvider, { useTracking } from './Tracking/TrackingContext';
import AppBar from './components/AppBar/AppBar';
import WongaFooter from './components/Footer/WongaFooter';
import UrgentNotificationBanner from './components/UrgentNotificationBanner/UrgentNotificationBanner';
import ABtestingProvider from './contexts/ABtestingContext';
import { AppSettingsContextProvider, useAppSettings } from './contexts/AppSettingsContext';
import LimiterProvider from './contexts/LimiterContext';
import AuthContextProvider, { RequireAuth } from './contexts/AuthContext';
import SeoContextProvider from './contexts/SeoContext';
import SnackBarProvider from './contexts/SnackBarContext';
import { PostProcessing, concurrentLoanJourney, l0journey, lnJourney, lnJourneyShortened } from './pages/Wizard/JourneyConfig/JourneyConfigs';
import { limpJourney } from './pages/Home/LimpWizard/JourneyConfig/LimpJourneyConfigs';
import Home from './pages/Home/Home';
import Base from './components/Base/base';
import SliderWithFlap from './components/Sliders/slider-with-flap/slider-with-flap';
import { getPollingMode } from './configCat/utils';
import Landing from './pages/MyLoan/Landing/Landing';
import { LimpJourneyRoutes, PostProcessingRoutes, RouterRoutes } from './utils/RouterRoutes';
import SkeletonLoader from './cms-pages/cms-components/SkeletonLoader/SkeletonLoader';
import { useVersionChecker } from './hooks/useVersionChecker';

const PaymentDialogProvider = lazy(() => import('./contexts/PaymentDialogContext'));
const MyDetails = lazy(() => import('./pages/MyDetails/MyDetails'));

const MyLoan = lazy(() => import('./pages/MyLoan/MyLoan'));
const AccountSummary = lazy(() => import('./pages/MyLoan/AccountSummary/AccountSummary'));
const LoanDocuments = lazy(() => import('./pages/MyLoan/LoanDocuments/LoanDocument'));
const DocTypeSelection = lazy(() => import('./pages/MyLoan/LoanDocuments/DocTypeSelection/DocTypeSelection'));
const LegalDocumentsView = lazy(() => import('./pages/MyLoan/LoanDocuments/LegalDocumentsView/LegalDocumentsView'));
const StatementsView = lazy(() => import('./pages/MyLoan/LoanDocuments/StatementsView/StatementsView'));
const PreviousLoansView = lazy(() => import('./pages/MyLoan/LoanDocuments/PreviousLoansView/PreviousLoansView'));
const CancelledDeclinedApplicationsView = lazy(() => import('./pages/MyLoan/LoanDocuments/CancelledDeclinedApplicationsView/CancelledDeclinedApplicationsView'));

const StatementPrint = lazy(() => import('./pages/MyLoan/LoanDocuments/StatementsView/StatementPrint/StatementPrint'));
const PaidUpLetterPrint = lazy(() => import('./pages/MyLoan/LoanDocuments/LegalDocumentsView/PaidUpLetterPrint/PaidUpLetterPrint'));
const LegalDocumentPrint = lazy(() => import('./pages/MyLoan/LoanDocuments/LegalDocumentsView/LegalDocumentPrint/LegalDocumentPrint'));
const SettlementLetterPdfView = lazy(() => import('./pages/MyLoan/AccountSummary/SettlementLetterPdf/SettlementLetterPdfView'));

const Register = lazy(() => import('./pages/Register/Register'));
const Login = lazy(() => import('./pages/Login/Login'));
const ConfirmIDNumber = lazy(() => import('./pages/Login/ViaOTP/ConfirmIDNumber'));
const ConfirmOTP = lazy(() => import('./pages/Login/ViaOTP/ConfirmOTP'));
const MissingCellNumber = lazy(() => import('./pages/Login/ViaOTP/MissingCellNumber'));
const ForgotPassword = lazy(() => import('./pages/ForgotPassword/ForgotPassword'));
const ForgotPasswordEmailResend = lazy(() => import('./pages/ForgotPassword/ForgotPasswordEmailResend'));
const ConfirmPasswordReset = lazy(() => import('./pages/ForgotPassword/ConfirmPasswordReset/ConfirmPasswordReset'));
const ConfirmChangeEmail = lazy(() => import('./pages/ChangeEmail/ConfirmChangeEmail/ConfirmChangeEmail'));

const Wizard = lazy(() => import('./pages/Wizard/Wizard'));
const LeadGenRedirect = lazy(() => import('./pages/Wizard/JourneyPages/LeadGen/LeadGenRedirect'));
const Limiter = lazy(() => import('./pages/Wizard/JourneyPages/Limiter'));

const LimpWizard = lazy(() => import('./pages/Home/LimpWizard/LimpWizard'));
const LegalBrillianceLanding = lazy(() => import("./pages/Home/LimpWizard/LegalBrillianceLanding"));

const PageNotFound = lazy(() => import('./pages/PageNotFound/PageNotFound'));

const CmsRoutes = lazy(() => import('./cms-pages/CmsRoutes'));
const ApolloWrapper = lazy(() => import('./apolloClient/ApolloWrapper'));

function App() {
  const { TrackError } = useTracking();
  const appSettings = useAppSettings();

  const logger = {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    log: (level: LogLevel, eventId: number, message: string, exception?: any) => {
      if (level === LogLevel.Error) {
        TrackError(exception ?? message, `ConfigCat Error (Event ID: ${eventId})`);
      }
    },
  };

  return (
    /* eslint-disable @typescript-eslint/no-empty-function */
    <AppSettingsContextProvider>
      <TrackingContextProvider>
        <ConfigCatProvider sdkKey={appSettings.configCat.sdkKey} pollingMode={getPollingMode(appSettings.configCat.pollingMode)} options={{
          logger, pollIntervalSeconds: appSettings.configCat.pollingInterval, setupHooks(hooks) {
            hooks.on('clientReady', () => console.log("Client is Ready"))
          },
          baseUrl: appSettings.configCat.baseUrl
        }}>
          <ABtestingProvider>
            <LimiterProvider>
              <SnackBarProvider>
                <AuthContextProvider>
                  <SeoContextProvider>
                    <AppRoutes />
                  </SeoContextProvider>
                </AuthContextProvider>
              </SnackBarProvider>
            </LimiterProvider>
          </ABtestingProvider>
        </ConfigCatProvider>
      </TrackingContextProvider>
    </AppSettingsContextProvider >
  );
}

function AppRoutes() {
  useVersionChecker();
  const location = useLocation();
  const showHeader = !(
    location.pathname.includes(RouterRoutes.statementPrint) ||
    location.pathname.includes(RouterRoutes.paidUpLetterPrint) ||
    location.pathname.includes(RouterRoutes.legalDocumentPrint) ||
    location.pathname.includes(RouterRoutes.settlementLetterPrint)
  );
  const declinedRoute = PostProcessing.find((config) => config.path === PostProcessingRoutes.declined);
  const showFooter = !(
    location.pathname.includes(RouterRoutes.statementPrint) ||
    location.pathname.includes(RouterRoutes.paidUpLetterPrint) ||
    location.pathname.includes(RouterRoutes.legalDocumentPrint) ||
    location.pathname.includes(RouterRoutes.settlementLetterPrint) ||
    location.pathname.includes(RouterRoutes.newJourney) ||
    location.pathname.includes(RouterRoutes.existingJourney) ||
    location.pathname.includes(RouterRoutes.concurrentJourney)
  );

  return (
    <>
      <UrgentNotificationBanner />
      {showHeader && <AppBar />}
      <Routes>
        <Route path={RouterRoutes.home} element={<Home />}>
          <Route path={RouterRoutes.home} element={<SliderWithFlap updateLoanValues={() => { }} />} />
        </Route>

        <Route path={RouterRoutes.home} element={<Base />}>
          <Route path={RouterRoutes.limiter} element={<Limiter />} />
          <Route path={RouterRoutes.login} element={<Login />} />
          <Route path={RouterRoutes.confirmIdNumber} element={<ConfirmIDNumber />} />
          <Route path={RouterRoutes.confirmOTP} element={<ConfirmOTP />} />
          <Route path={RouterRoutes.MissingCellNumber} element={<MissingCellNumber />} />
          <Route path={RouterRoutes.register} element={<Register />} />

          <Route path={RouterRoutes.confirmChangeEmail + '/:pin'} element={<ConfirmChangeEmail />} />
          <Route path={RouterRoutes.recoverPassword} element={<ForgotPassword />} />
          <Route path={RouterRoutes.confirmPasswordReset + '/:pin'} element={<ConfirmPasswordReset />} />

          <Route path={RouterRoutes.newJourney} element={<Wizard />}>
            {l0journey.map((config, index) => (
              <Route key={index} path={config.path} element={config.element} />
            ))}
            {PostProcessing.map((config, index) => (
              <Route key={index} path={config.path} element={config.element} />
            ))}
            {declinedRoute && (
              <Route
                path={`${PostProcessingRoutes.declined}/:loanId`}
                element={cloneElement(declinedRoute.element)}
              />
            )}
          </Route>
          <Route path={RouterRoutes.existingJourney} element={<Wizard />}>
            {[...lnJourney, ...lnJourneyShortened].map((config, index) => (
              <Route key={index} path={config.path} element={config.element} />
            ))}
            {PostProcessing.map((config, index) => (
              <Route key={index} path={config.path} element={config.element} />
            ))}
            {declinedRoute && (
              <Route
                path={`${PostProcessingRoutes.declined}/:loanId`}
                element={cloneElement(declinedRoute.element)}
              />
            )}
          </Route>
          <Route path={RouterRoutes.concurrentJourney} element={<Wizard />}>
            {concurrentLoanJourney.map((config, index) => (
              <Route key={index} path={config.path} element={config.element} />
            ))}
            {PostProcessing.map((config, index) => (
              <Route key={index} path={config.path} element={config.element} />
            ))}
            {declinedRoute && (
              <Route
                path={`${PostProcessingRoutes.declined}/:loanId`}
                element={cloneElement(declinedRoute.element)}
              />
            )}
          </Route>

          {/* Legal Brilliance Journey */}
          <Route path={`${RouterRoutes.legalBrillianceJourney}/${LimpJourneyRoutes.landing}`} element={<LegalBrillianceLanding />} />
          <Route path={RouterRoutes.legalBrillianceJourney} element={<LimpWizard />}>
            {limpJourney.map((config, index) => (
              <Route key={index} path={config.path} element={config.element} />
            ))}
          </Route>

          {/* My loan */}
          <Route
            path={RouterRoutes.myLoan + '/*'}
            element={
              <RequireAuth>
                <MyLoan />
              </RequireAuth>
            }
          >
            <Route index element={<Landing />} />
            <Route
              path={':loanId/*'}
              element={
                <PaymentDialogProvider>
                  <AccountSummary />
                </PaymentDialogProvider>
              }
            />
            <Route path={RouterRoutes.loanDocuments} element={<LoanDocuments />} />
            <Route path={`${RouterRoutes.loanDocuments}/${RouterRoutes.previousLoans}`} element={<PreviousLoansView />} />
            <Route path={`${RouterRoutes.loanDocuments}/${RouterRoutes.cancelledDeclinedApplications}`} element={<CancelledDeclinedApplicationsView />} />
            <Route path={`${RouterRoutes.loanDocuments}/:loanId`} element={<DocTypeSelection />} />
            <Route path={`${RouterRoutes.loanDocuments}/:loanId/${RouterRoutes.legalDocuments}`} element={<LegalDocumentsView />} />
            <Route path={`${RouterRoutes.loanDocuments}/:loanId/${RouterRoutes.statements}`} element={<StatementsView />} />
          </Route>

          {/* My Profile */}
          <Route
            path={RouterRoutes.myDetails + '/*'}
            element={
              <RequireAuth>
                <MyDetails />
              </RequireAuth>
            }
          />

          <Route path={RouterRoutes.statementPrint} element={<StatementPrint />} />
          <Route path={RouterRoutes.paidUpLetterPrint} element={<PaidUpLetterPrint />} />
          <Route path={RouterRoutes.legalDocumentPrint} element={<LegalDocumentPrint />} />
          <Route path={RouterRoutes.settlementLetterPrint} element={<SettlementLetterPdfView />} />

          <Route path={RouterRoutes.emailResend} element={<ForgotPasswordEmailResend />} />
          <Route path={RouterRoutes.leadAccept} element={<LeadGenRedirect />} />
        </Route>

        <Route path='/*' element={
          <Suspense fallback={<SkeletonLoader />}>
            <ApolloWrapper>
              <CmsRoutes />
            </ApolloWrapper>
          </Suspense>
        }>
        </Route>

        <Route path="*" element={<PageNotFound />} />
      </Routes>
      {showFooter && <WongaFooter />}
    </>
  );
}

export default App;
